<template>
	<div class="search-input">
		<i class="bi bi-search search-input-icon"></i>
		<input
			ref="field"
			v-model="value"
			type="text"
			class="form-control form-control-sm search-input-field"
			:placeholder="placeholder"
			:aria-label="ariaLabel"
			:autofocus="autofocus"
			@keydown.enter.prevent="$emit('submit', modelValue)"
			@keydown.esc.prevent="clear"
		/>
		<button
			v-if="modelValue !== ''"
			type="button"
			class="search-input-clear"
			title="Clear search"
			aria-label="Clear search"
			@click="clear"
		>
			<i class="bi bi-x-lg"></i>
		</button>
	</div>
</template>

<script setup lang="ts">
// The one search box both surfaces use.
//
// It is deliberately dumb: the text in, the text out, and three events for the
// things a box does — the operator submitting it, clearing it, and typing into
// it through v-model. It performs no request, owns no result list and knows
// nothing about what is being searched, so the same box sits over the ledger's
// finder and over Reveal's site list.
//
// Escaping and the clear button run the same `clear`, so the two ways out of a
// search behave identically, and the box takes the focus back afterwards: the
// operator who cleared is about to type again.
import { computed, ref } from "vue";

interface Props {
	modelValue: string;
	placeholder?: string;
	ariaLabel?: string;
	/** Take the focus when the box mounts. The panes whose whole purpose is the
	 * box (Map Review's Search tab) turn this on. */
	autofocus?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	placeholder: "",
	ariaLabel: "Search",
	autofocus: false,
});

const emit = defineEmits<{
	"update:modelValue": [value: string];
	submit: [value: string];
	clear: [];
}>();

const field = ref<HTMLInputElement | null>(null);

const value = computed({
	get: () => props.modelValue,
	set: (next: string) => emit("update:modelValue", next),
});

function clear() {
	emit("update:modelValue", "");
	emit("clear");
	field.value?.focus();
}
</script>

<style scoped>
/* A compact Bootstrap input with the search glyph inside its left edge and the
   clear button inside the right, so the box keeps the form-control look both
   panes already use while reading as a search field. */
.search-input {
	position: relative;
	display: flex;
	align-items: center;
	width: 100%;
}

.search-input-icon {
	position: absolute;
	left: 0.5rem;
	color: #6c757d;
	font-size: 0.75rem;
	pointer-events: none;
}

.search-input-field {
	padding-left: 1.6rem;
	padding-right: 1.6rem;
	font-size: 0.8125rem;
}

.search-input-clear {
	position: absolute;
	right: 0.35rem;
	border: 0;
	background: transparent;
	color: #adb5bd;
	font-size: 0.6875rem;
	line-height: 1;
	padding: 0.2rem;
	cursor: pointer;
}

.search-input-clear:hover {
	color: #495057;
}
</style>
